import {DateTime} from 'luxon';
import React, {useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {EventHandler, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {SimpleDropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {
  SimpleDropdownWithHeader,
  SimpleDropdownWithHeaderProps,
} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownWithHeader';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {
  DateRangePeriodsEnum,
  StartAndEndDates,
  StartAndEndDatesWithPeriod,
} from '../../../models/dates/dateIntervalModel';
import {DropdownItemSeparator} from '../dropdowns/dropdownItemSeparator';
import {DateRangeComboBoxDropdownPeriodItem} from './dateRangeComboBoxDropdownPeriodItem';
import {DateRangePicker} from './dateRangePicker';

/*
 * Types.
 */

interface DateRageComboBoxProps {
  startDate?: DateTime;
  endDate?: DateTime;
  onDateSelect: (dates: StartAndEndDatesWithPeriod, closeDropdown?: boolean) => void;
  onDateChange?: HandlerOf<StartAndEndDates>;
  onCancel: Handler;
  calendarWeekStartDay: CalendarWeekDaysEnum;
  popoverTransferProps: PopoverTransferProps;
  placement?: SimpleDropdownWithHeaderProps['placement'];
}

/*
 * Styles.
 */

const Period = styled.div`
  grid-area: right-icon;
  margin: 2px 0 0 20px;
  color: ${(p) => p.theme.greys.shade60};
  min-width: 180px;
  text-align: right;
`;

const StyledIcon = styled(Icon)<{$margin?: string}>`
  color: ${(p) => p.theme.greys.shade60};
  margin: ${(props) => props.$margin};
`;

const RangePickerWrap = styled.div`
  padding: 15px;
`;

/*
 * Component.
 */

const dateRangeHeaderTitle = <Trans id="date-range">Date range</Trans>;
const customDateRangeHeaderTitle = <Trans id="custom-date-range">Custom date range</Trans>;
const customRangeItemLabel = <Trans id="custom-range">Custom range</Trans>;

export const DateRangeComboBoxDropdown = (props: DateRageComboBoxProps) => {
  const {
    popoverTransferProps,
    calendarWeekStartDay,
    placement,
    onDateSelect,
    onDateChange,
    onCancel,
    startDate,
    endDate,
  } = props;
  const today = useRef(DateTime.local());
  const [isCustomRange, setIsCustomRange] = useState(false);
  const periods = computePeriods(today.current, calendarWeekStartDay);

  const onCustomPeriodChange: EventHandler = (e) => {
    e.preventDefault();
    setIsCustomRange(true);
  };

  function renderCustomRange() {
    return (
      <RangePickerWrap>
        <DateRangePicker
          calendarWeekStartDay={calendarWeekStartDay}
          selectedStartDate={startDate}
          selectedEndDate={endDate}
          onConfirm={(dates) => onDateSelect(dates, true)}
          onChange={onDateChange}
          onCancel={onCancel}
        />
      </RangePickerWrap>
    );
  }

  function renderPeriods() {
    return (
      <>
        {periods.map((period) => (
          <DateRangeComboBoxDropdownPeriodItem
            key={period.key}
            label={period.label}
            period={period.period}
            onDateSelect={onDateSelect}
          />
        ))}
        <DropdownItemSeparator />
        <SimpleDropdownItem
          checkmarkLabel={customRangeItemLabel}
          renderRightContent={() => (
            <Period>
              <StyledIcon name="chevronRightSmall" $margin="0 0 0 auto" />
            </Period>
          )}
          onSelect={onCustomPeriodChange}
        >
          {customRangeItemLabel}
        </SimpleDropdownItem>
      </>
    );
  }

  return (
    <SimpleDropdownWithHeader
      title={isCustomRange ? customDateRangeHeaderTitle : dateRangeHeaderTitle}
      placement={placement}
      interactionId="range-picker"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...popoverTransferProps}
    >
      {isCustomRange ? renderCustomRange() : renderPeriods()}
    </SimpleDropdownWithHeader>
  );
};

/*
 * Helpers.
 */

function calculateLastQuarterMonthOffset(date: DateTime) {
  const {month} = date;
  const offset = month % 3;

  if (offset === 0) {
    return 3;
  }

  return offset;
}

const computePeriods = (today: DateTime, calendarWeekStartDay: CalendarWeekDaysEnum) => [
  {
    key: DateRangePeriodsEnum.TODAY,
    label: <Trans id="today-range-picker-item">Today</Trans>,
    period: computeDateRangePeriod(DateRangePeriodsEnum.TODAY, calendarWeekStartDay, today),
  },
  {
    key: DateRangePeriodsEnum.YESTERDAY,
    label: <Trans id="yesterday-range-picker-item">Yesterday</Trans>,
    period: computeDateRangePeriod(DateRangePeriodsEnum.YESTERDAY, calendarWeekStartDay, today),
  },
  // Only Monday and Sunday as start of a week is supported at the moment
  {
    key: DateRangePeriodsEnum.THIS_WEEK_TO_DATE,
    label: <Trans id="this-week-to-date-range-picker-item">This week to date</Trans>,
    period: computeDateRangePeriod(DateRangePeriodsEnum.THIS_WEEK_TO_DATE, calendarWeekStartDay, today),
  },
  {
    key: DateRangePeriodsEnum.LAST_SEVEN_DAYS,
    label: <Trans id="last-7-days-range-picker-item">Last 7 days</Trans>,
    period: computeDateRangePeriod(DateRangePeriodsEnum.LAST_SEVEN_DAYS, calendarWeekStartDay, today),
  },
  // Only Monday and Sunday as start of a week is supported at the moment
  {
    key: DateRangePeriodsEnum.LAST_WEEK,
    label: <Trans id="last-week-range-picker-item">Last week</Trans>,
    period: computeDateRangePeriod(DateRangePeriodsEnum.LAST_WEEK, calendarWeekStartDay, today),
  },
  {
    key: DateRangePeriodsEnum.LAST_MONTH,
    label: <Trans id="last-month-range-picker-item">Last month</Trans>,
    period: computeDateRangePeriod(DateRangePeriodsEnum.LAST_MONTH, calendarWeekStartDay, today),
  },
  {
    key: DateRangePeriodsEnum.LAST_QUARTER,
    label: <Trans id="last-quarter-range-picker-item">Last quarter</Trans>,
    period: computeDateRangePeriod(DateRangePeriodsEnum.LAST_QUARTER, calendarWeekStartDay, today),
  },
  {
    key: DateRangePeriodsEnum.LAST_YEAR,
    label: <Trans id="last-year-range-picker-item">Last year</Trans>,
    period: computeDateRangePeriod(DateRangePeriodsEnum.LAST_YEAR, calendarWeekStartDay, today),
  },
  {
    key: DateRangePeriodsEnum.THIS_YEAR_TO_DATE,
    label: <Trans id="this-year-to-date-range-picker-item">This year to date</Trans>,
    period: computeDateRangePeriod(DateRangePeriodsEnum.THIS_YEAR_TO_DATE, calendarWeekStartDay, today),
  },
];

function computeDateRangePeriod(
  period: DateRangePeriodsEnum,
  calendarWeekStartDay: CalendarWeekDaysEnum,
  today: DateTime,
): StartAndEndDatesWithPeriod {
  const lastQuarterMonthOffset = calculateLastQuarterMonthOffset(today);

  switch (period) {
    case DateRangePeriodsEnum.TODAY:
      return {
        period,
        startDate: today.startOf('day'),
      };

    case DateRangePeriodsEnum.YESTERDAY:
      return {
        period,
        startDate: today.minus({days: 1}).startOf('day'),
      };

    case DateRangePeriodsEnum.THIS_WEEK_TO_DATE:
      return {
        period,
        startDate:
          calendarWeekStartDay === CalendarWeekDaysEnum.SUNDAY &&
          today.weekdayLong.toLowerCase() === CalendarWeekDaysEnum.SUNDAY
            ? today.startOf('day')
            : today
                .startOf('week')
                .minus({days: calendarWeekStartDay === CalendarWeekDaysEnum.SUNDAY ? 1 : 0}),
        endDate: today.endOf('day'),
      };

    case DateRangePeriodsEnum.LAST_SEVEN_DAYS:
      return {
        period,
        startDate: today.minus({days: 7}).startOf('day'),
        endDate: today.minus({day: 1}).endOf('day'),
      };

    case DateRangePeriodsEnum.LAST_WEEK:
      return {
        period,
        startDate: today
          .minus({week: 1})
          .startOf('week')
          .minus({days: calendarWeekStartDay === CalendarWeekDaysEnum.SUNDAY ? 1 : 0}),
        endDate: today
          .minus({week: 1})
          .startOf('week')
          .plus({days: 6})
          .minus({days: calendarWeekStartDay === CalendarWeekDaysEnum.SUNDAY ? 1 : 0})
          .endOf('day'),
      };

    case DateRangePeriodsEnum.LAST_MONTH:
      return {
        period,
        startDate: today.minus({months: 1}).startOf('month'),
        endDate: today.minus({months: 1}).endOf('month'),
      };

    case DateRangePeriodsEnum.LAST_QUARTER:
      return {
        period,
        startDate: today.minus({month: lastQuarterMonthOffset + 2}).startOf('month'),
        endDate: today.minus({month: lastQuarterMonthOffset}).endOf('month'),
      };

    case DateRangePeriodsEnum.LAST_YEAR:
      return {
        period,
        startDate: today.minus({year: 1}).startOf('year'),
        endDate: today.minus({year: 1}).endOf('year').endOf('day'),
      };

    case DateRangePeriodsEnum.THIS_YEAR_TO_DATE:
      return {
        period,
        startDate: today.startOf('year'),
        endDate: today.endOf('day'),
      };

    // Default statement, this should not happen.
    default:
      throw new UnreachableError(period);
  }
}
